'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { MartiniLoader } from '@/components/MartiniLoader'
import { useWorkspaceContext } from '@/store/workspaceContextStore'

// Last-resort backstop only — the popstate and pathname guards below cover the
// known cancellation paths. This just guarantees a dead navigation can never
// leave the workspace surfaces permanently covered.
const SWITCH_STUCK_MS = 15_000

/**
 * Covers the workspace surfaces while a switch picked in the navbar is still
 * navigating (ENG-723).
 *
 * `/projects` and `/library` derive their workspace from the URL, so between
 * the pick and the route commit the PREVIOUS workspace's projects and olive
 * balance are still on screen — under a switcher chip that already names the
 * new one. Both destination segments are client pages with no loading boundary,
 * so on a slow link that window ran to ~10s of confidently wrong data.
 *
 * Lives in AppShell (below the header, which stays interactive) so it survives
 * the origin page's unmount while the destination chunk downloads. Every route
 * that scopes itself to a workspace ends the switch by syncing the store from
 * its URL; the guards here end it when the navigation is abandoned instead.
 *
 * Mounted unconditionally rather than alongside the header, so those guards
 * always run — a switch that outlived its overlay would leave the store
 * pending and cover the next workspace surface for no reason. The `top-12`
 * offset assumes the shared header, which every surface holding the switcher
 * (and every surface it can navigate to) renders.
 */
export function WorkspaceSwitchOverlay() {
  const pathname = usePathname()
  const pendingContextId = useWorkspaceContext(s => s.pendingContextId)
  const switchTargetPathname = useWorkspaceContext(s => s.switchTargetPathname)
  const endSwitch = useWorkspaceContext(s => s.endSwitch)
  const abandonSwitch = useWorkspaceContext(s => s.abandonSwitch)
  const isSwitching = pendingContextId !== null

  // Back/forward during the in-flight switch: the user is back on a route that
  // is already interactive and is NOT the workspace they picked, so put the
  // workspace back rather than leaving the chip naming one they never saw.
  useEffect(() => {
    if (!isSwitching) return
    const onPopState = () => abandonSwitch()
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [isSwitching, abandonSwitch])

  // Arrived where this switch was headed — it landed, so keep the pick. This
  // is the guard that ends a normal switch: the destination pages only call
  // setContext when their workspace DIFFERS from the store's, and beginSwitch
  // already set it, so they stay quiet on the happy path.
  //
  // Matching the TARGET rather than "the path changed at all" is what keeps a
  // slow earlier switch from settling a newer one: pick A, pick B before A
  // commits, then A commits — that lands on A's path, not B's, so the overlay
  // stays up over A's content until B (the workspace the chip names) arrives.
  // Landing anywhere else is handled by setContext (a redirect's page syncs its
  // own workspace, which differs from the pick) or by the stuck backstop.
  useEffect(() => {
    if (!isSwitching) return
    if (pathname === switchTargetPathname) endSwitch()
  }, [pathname, isSwitching, switchTargetPathname, endSwitch])

  // Backstop for a navigation that never lands. Keyed on the pick so a second
  // pick restarts the window instead of inheriting the first one's remainder.
  useEffect(() => {
    if (!isSwitching) return
    const timeoutId = window.setTimeout(() => abandonSwitch(), SWITCH_STUCK_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isSwitching, pendingContextId, abandonSwitch])

  if (!isSwitching) return null

  return (
    // Starts below the h-12 header so the switcher you just used stays visible
    // and usable — only the workspace-scoped content is covered.
    <div
      className="fixed inset-x-0 bottom-0 top-12 z-50 flex flex-col items-center justify-center gap-2 bg-background px-6"
      aria-busy="true"
      aria-live="polite"
    >
      <MartiniLoader className="text-foreground" />
      <span className="text-sm text-muted-foreground">Switching workspace...</span>
    </div>
  )
}
